import { notifyOnError } from '~/rage/decorators';
import { IObservable, Subject } from '~/rage/utils';
import {
	IVehicleMarketAdvertBase,
	IVehicleMarketAdvertDetails,
} from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { IPlayerService } from '../PlayerService';
import { RpcService } from '../RpcService';
import { VarsService } from '../VarsService/VarsService';
import { IVehicleMarketService } from './vehicleMarketServiceTypes';

export class VehicleMarketService implements IVehicleMarketService {
	private vehicleAdvertPrice = new Subject<number>(0);
	private maxSellTimeMs = new Subject<number>(0);
	private currentAdvert = new Subject<
		(IVehicleMarketAdvertDetails & IVehicleMarketAdvertBase) | null
	>(null);
	private currentAdvertGeneration = 0;

	constructor(
		private readonly rpc: RpcService,
		private readonly vars: VarsService,
		private readonly gameConfig: GameConfigService,
		private readonly backend: BackendService,
		private readonly player: IPlayerService,
	) {
		this.gameConfig.getConfigs().subscribe((config) => {
			if (!config || !config.vehiclesMarket) return;
			this.vehicleAdvertPrice.next(config.vehiclesMarket.vehicleAdvertPrice);
			this.maxSellTimeMs.next(config.vehiclesMarket.maxSellTimeMs);
		});

		this.vars
			.get<IVehicleMarketAdvertBase | null>('vehicleMarket:currentAdvert', null)
			.subscribe(this.handleCurrentAdvertChange.bind(this));
	}

	private async handleCurrentAdvertChange(
		advertBase: IVehicleMarketAdvertBase | null,
	): Promise<void> {
		const generation = ++this.currentAdvertGeneration;
		await this.player.waitForCharacterSelect();
		await this.gameConfig.waitForInit();

		if (generation !== this.currentAdvertGeneration) return;
		if (!advertBase) {
			this.currentAdvert.next(null);
			return;
		}

		const advertDetails =
			await this.backend.invoke<IVehicleMarketAdvertDetails>(
				'vehicleMarket:getAdvertDetails',
				{ vehicleId: advertBase.vehicleId },
			);

		if (generation !== this.currentAdvertGeneration) return;
		this.currentAdvert.next({
			...advertBase,
			...advertDetails,
		});
	}

	public getCurrentAdvert(): IObservable<
		(IVehicleMarketAdvertBase & IVehicleMarketAdvertDetails) | null
	> {
		return this.currentAdvert;
	}

	@notifyOnError
	public async createAdvert(price: number): Promise<void> {
		await this.rpc.invokeServer('vehicleMarket:createAdvert', {
			price,
		});
	}

	@notifyOnError
	public async editAdvertPrice(
		vehicleId: string,
		newPrice: number,
	): Promise<void> {
		await this.rpc.invokeServer('vehicleMarket:editAdvertPrice', {
			vehicleId,
			price: newPrice,
		});
	}

	@notifyOnError
	public async removeAdvert(vehicleId: string): Promise<void> {
		await this.rpc.invokeServer('vehicleMarket:removeAdvert', {
			vehicleId,
		});
	}

	@notifyOnError
	public async buyAdvert(vehicleId: string): Promise<void> {
		await this.rpc.invokeServer('vehicleMarket:buyAdvert', {
			vehicleId,
		});
	}

	public getVehicleAdvertPrice(): IObservable<number> {
		return this.vehicleAdvertPrice;
	}

	public getMaxSellTimeMs(): IObservable<number> {
		return this.maxSellTimeMs;
	}

	public getIsCreatingNewAdvert(): IObservable<boolean> {
		return this.vars.get('vehicleMarket:isCreatingNewAdvert', false);
	}

	public async exitMenu(): Promise<void> {
		await this.rpc.invokeClient('vehicleMarket:exitMenu');
	}
}
